/*
 * Cart drawer extras — vanilla web components, zero new dependencies.
 *
 * Reuses the theme's existing primitives (window.routes, fetchConfig pattern,
 * the cart-drawer-items section-render descriptors) so every mutation re-renders
 * the drawer the same way a quantity change does. Loaded only when
 * settings.cart_type == 'drawer'.
 */
(function () {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Shopify's `t` filter HTML-escapes translations (e.g. an apostrophe becomes
  // &#39;). These strings are rendered with textContent, which would show the raw
  // entity, so decode them once here — covering every current and future string.
  const STR = (function (raw) {
    const out = {};
    const ta = document.createElement('textarea');
    Object.keys(raw || {}).forEach((k) => {
      if (typeof raw[k] === 'string') {
        ta.innerHTML = raw[k];
        out[k] = ta.value;
      } else {
        out[k] = raw[k];
      }
    });
    return out;
  })(window.cartDrawerStrings);

  // ---- Shared helpers -------------------------------------------------------

  function cartFetchConfig(type) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + (type || 'json') },
    };
  }

  // Re-render the drawer sections from a parsed cart state (with .sections),
  // reusing the cart-drawer-items descriptors so the whole .drawer__inner +
  // cart icon bubble update — identical to the quantity-change path.
  function renderDrawerSections(parsedState) {
    const items = document.querySelector('cart-drawer-items');
    if (!items || !parsedState || !parsedState.sections) return;
    items.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      const html = parsedState.sections[section.section];
      if (html == null) return;
      target.innerHTML = items.getSectionInnerHTML(html, section.selector);
    });
    document.dispatchEvent(new CustomEvent('cart-drawer:rendered', { detail: parsedState }));
  }

  function drawerSectionNames() {
    const items = document.querySelector('cart-drawer-items');
    if (!items) return ['cart-drawer', 'cart-icon-bubble'];
    return items.getSectionsToRender().map((s) => s.section);
  }

  // Page-cart (cart.js CartItems) equivalents, so the inline variant editor can
  // re-render the /cart page sections instead of the drawer's. <cart-drawer-items>
  // has its own tag, so this only matches the page's <cart-items>.
  function pageItemsEl() {
    return document.querySelector('cart-items');
  }
  function pageSectionNames() {
    const items = pageItemsEl();
    if (!items || typeof items.getSectionsToRender !== 'function') {
      return ['main-cart-items', 'main-cart-footer', 'cart-icon-bubble'];
    }
    return items.getSectionsToRender().map((s) => s.section);
  }
  function renderPageSections(parsedState) {
    const items = pageItemsEl();
    if (!items || !parsedState || !parsedState.sections) return;
    items.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      const html = parsedState.sections[section.section];
      if (html == null) return;
      target.innerHTML = items.getSectionInnerHTML(html, section.selector);
    });
    document.dispatchEvent(new CustomEvent('cart:rendered', { detail: parsedState }));
  }

  function formatMoney(amount, currency) {
    const cur = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: cur,
      }).format(amount);
    } catch (e) {
      return amount + ' ' + cur;
    }
  }

  // Open a containing <details> and move focus to a field within it.
  function openToolPanel(el, focusTarget) {
    const details = el && el.closest('details');
    if (details) details.open = true;
    if (focusTarget) focusTarget.focus();
  }

  // Scroll an element fully into the drawer's scrolling region (reveal its bottom,
  // or align the top if it's taller than the viewport). Used when a tool opens or
  // an error appears below the fold.
  function scrollIntoCartView(el, behavior) {
    if (!el) return;
    behavior = behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    const container = el.closest('cart-drawer-items');
    if (!container) {
      el.scrollIntoView({ block: 'nearest', behavior });
      return;
    }
    const margin = 8;
    const cr = container.getBoundingClientRect();
    const dr = el.getBoundingClientRect();
    let delta = 0;
    if (dr.bottom > cr.bottom - margin) {
      delta = dr.bottom - (cr.bottom - margin);
      const maxUp = dr.top - (cr.top + margin);
      if (delta > maxUp) delta = maxUp;
    } else if (dr.top < cr.top + margin) {
      delta = dr.top - (cr.top + margin);
    }
    if (delta !== 0) container.scrollBy({ top: delta, behavior });
  }

  // ---- Free shipping bar ----------------------------------------------------

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.cart-freeship__fill');
      if (!fill) return;
      const pct = Math.max(0, Math.min(100, parseFloat(this.dataset.percent) || 0));
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        fill.style.width = pct + '%';
        return;
      }
      fill.style.width = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.width = pct + '%';
        });
      });
    }
  }
  customElements.define('free-shipping-bar', FreeShippingBar);

  // ---- Estimated delivery ---------------------------------------------------

  class EstimatedDelivery extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      if (!Number.isFinite(min) || !Number.isFinite(max) || max < 1) {
        this.hidden = true;
        return;
      }
      const fmt = { weekday: 'short', month: 'short', day: 'numeric' };
      const lang = document.documentElement.lang || undefined;
      const today = new Date();
      const start = this.addBusinessDays(today, Math.max(1, min));
      const end = this.addBusinessDays(today, Math.max(min, max));
      const label = this.dataset.label || 'Estimated delivery';
      const sameDay = start.toDateString() === end.toDateString();
      const value = sameDay
        ? start.toLocaleDateString(lang, fmt)
        : start.toLocaleDateString(lang, fmt) + ' – ' + end.toLocaleDateString(lang, fmt);
      this.innerHTML =
        '<span class="cart-est-delivery__icon svg-wrapper" aria-hidden="true"></span>' +
        '<span class="cart-est-delivery__text"><span class="cart-est-delivery__label">' +
        this.escape(label) +
        '</span> <span class="cart-est-delivery__value">' +
        this.escape(value) +
        '</span></span>';
    }

    addBusinessDays(from, days) {
      const d = new Date(from.getTime());
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) added++;
      }
      return d;
    }

    escape(s) {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    }
  }
  customElements.define('estimated-delivery', EstimatedDelivery);

  // ---- Discount code --------------------------------------------------------

  function currentDiscountCodes() {
    return Array.from(document.querySelectorAll('.cart-discount__remove[data-code]')).map((b) => b.dataset.code);
  }

  // Authoritative current code set (race-free) — the rendered chips are populated
  // asynchronously, so read from the cart JSON before stacking/removing codes.
  async function getAppliedCodes() {
    try {
      const cart = await (await fetch(root + 'cart.js')).json();
      return (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
    } catch (e) {
      return currentDiscountCodes();
    }
  }

  async function postDiscount(codes) {
    const res = await fetch(root + 'cart/update.js', {
      ...cartFetchConfig(),
      body: JSON.stringify({
        discount: codes.join(','),
        sections: drawerSectionNames().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const cart = await res.json();
    if (!res.ok) throw new Error((cart && cart.description) || 'Cart update failed');
    return cart;
  }

  function codeApplied(cart, code) {
    const c = code.trim().toLowerCase();
    const entry = (cart.discount_codes || []).find((d) => (d.code || '').toLowerCase() === c);
    return Boolean(entry && entry.applicable);
  }

  class CartDiscount extends HTMLElement {
    constructor() {
      super();
      this.onApply = this.onApply.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('.cart-discount__input');
      this.button = this.querySelector('.cart-discount__apply');
      this.errorEl = this.querySelector('.cart-discount__error');
      if (this.button) this.button.addEventListener('click', this.onApply);
      if (this.input) {
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.onApply();
          }
        });
      }
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
      if (msg) requestAnimationFrame(() => scrollIntoCartView(this.errorEl));
    }

    setLoading(on) {
      if (this.button) {
        this.button.classList.toggle('loading', on);
        this.button.disabled = on;
      }
    }

    async onApply() {
      const code = (this.input && this.input.value || '').trim();
      if (!code) {
        this.setError(STR.discountEmpty || 'Enter a discount code.');
        if (this.input) this.input.focus();
        return;
      }
      this.setError('');
      this.setLoading(true);
      try {
        const existing = await getAppliedCodes();
        const next = Array.from(new Set([...existing, code].map((c) => c.trim()))).filter(Boolean);
        const cart = await postDiscount(next);
        if (codeApplied(cart, code)) {
          // Success: clear the input and re-render so the discount shows in totals.
          if (this.input) this.input.value = '';
          renderDrawerSections(cart);
        } else {
          // Invalid: nothing applied, so don't re-render. Show the error right away
          // (and scroll it into view) — then drop the lingering non-applicable code
          // server-side in the background so the message isn't delayed by it.
          this.setError(STR.discountError || "This code can't be applied.");
          const stillThere = (cart.discount_codes || []).some(
            (d) => (d.code || '').toLowerCase() === code.toLowerCase()
          );
          if (stillThere) postDiscount(existing).catch(() => {});
        }
      } catch (e) {
        this.setError((e && e.message) || STR.discountError || "This code can't be applied.");
      } finally {
        this.setLoading(false);
      }
    }
  }
  customElements.define('cart-discount', CartDiscount);

  function escapeHtml(s) {
    const div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }

  const DISCOUNT_ICON =
    '<svg class="icon icon-discount" viewBox="0 0 12 12" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/></svg>';

  // Renders the applied-discount breakdown from the cart JSON — reliable across
  // every discount type and not dependent on Liquid:
  //   * code discounts (% / fixed): "CODE (-$amount)" + remove, amount read from
  //     cart_level_discount_applications (matched by title).
  //   * free-shipping codes: present in discount_codes but NOT in the
  //     applications list -> shown as "CODE · Free shipping" + remove.
  //   * automatic / script discounts: shown with their amount, not removable.
  class AppliedDiscounts extends HTMLElement {
    async connectedCallback() {
      try {
        const cart = await (await fetch(root + 'cart.js')).json();
        const codes = (cart.discount_codes || []).filter((d) => d.applicable);
        const apps = cart.cart_level_discount_applications || [];
        const lines = [];

        codes.forEach((d) => {
          const app = apps.find(
            (a) => a.type === 'discount_code' && (a.title || '').toLowerCase() === (d.code || '').toLowerCase()
          );
          lines.push({
            title: d.code,
            amount: app ? app.total_allocated_amount : 0,
            freeShipping: !app, // codes absent from the applications list are shipping discounts
            removable: true,
          });
        });

        apps
          .filter((a) => a.type !== 'discount_code')
          .forEach((a) => {
            lines.push({ title: a.title, amount: a.total_allocated_amount, freeShipping: false, removable: false });
          });

        if (!lines.length) {
          this.innerHTML = '';
          this.hidden = true;
          return;
        }
        this.hidden = false;

        const removeLabel = STR.discountRemove || 'Remove discount';
        const freeShipTxt = STR.discountFreeShipping || 'Free shipping';

        this.innerHTML =
          '<ul class="discounts list-unstyled" role="list">' +
          lines
            .map((l) => {
              const title = escapeHtml(l.title);
              let detail = '';
              if (l.amount > 0) detail = ' (-' + formatMoney(l.amount / 100) + ')';
              else if (l.freeShipping) detail = ' · ' + escapeHtml(freeShipTxt);
              const remove = l.removable
                ? '<button type="button" class="cart-discount__remove" data-code="' +
                  title +
                  '" aria-label="' +
                  escapeHtml(removeLabel) +
                  ' ' +
                  title +
                  '"><span aria-hidden="true">&times;</span></button>'
                : '';
              return (
                '<li class="discounts__discount discounts__discount--cart">' +
                DISCOUNT_ICON +
                '<span class="discounts__title">' +
                title +
                detail +
                '</span>' +
                remove +
                '</li>'
              );
            })
            .join('') +
          '</ul>';
      } catch (e) {
        this.hidden = true;
      }
    }
  }
  customElements.define('applied-discounts', AppliedDiscounts);

  // Remove a discount code (delegated — the remove buttons live in re-rendered Liquid).
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('.cart-discount__remove');
    if (!btn) return;
    e.preventDefault();
    const code = btn.dataset.code;
    btn.classList.add('loading');
    try {
      const existing = await getAppliedCodes();
      const next = existing.filter((c) => c.toLowerCase() !== (code || '').toLowerCase());
      const cart = await postDiscount(next);
      renderDrawerSections(cart);
    } catch (err) {
      console.error(err);
      btn.classList.remove('loading');
    }
  });

  // When a collapsible tool is opened, scroll its panel fully into the scrolling
  // region so it isn't clipped below the fold. `toggle` doesn't bubble, so listen
  // in the capture phase (which still reaches descendant targets).
  document.addEventListener(
    'toggle',
    (e) => {
      const details = e.target;
      if (!details || !details.classList || !details.classList.contains('cart-tool') || !details.open) return;
      // Wait for the panel to lay out, then reveal the whole opened tool.
      requestAnimationFrame(() => requestAnimationFrame(() => scrollIntoCartView(details)));
    },
    true
  );

  // ---- In-cart upsell -------------------------------------------------------

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('.cart-upsell__body');
      this.addEventListener('click', this.onClick.bind(this));
      // Variant-bound price: update the shown price when a recommendation's
      // variant <select> changes.
      this.addEventListener('change', (e) => {
        const select = e.target.closest && e.target.closest('.cart-upsell__select');
        if (!select) return;
        const item = select.closest('.cart-upsell__item');
        const opt = select.options[select.selectedIndex];
        if (!item || !opt) return;
        const wrap = item.querySelector('[data-upsell-price]');
        const nowEl = item.querySelector('[data-upsell-now]');
        const wasEl = item.querySelector('[data-upsell-was]');
        const price = Number(opt.dataset.price); // cents
        const compare = Number(opt.dataset.comparePrice); // cents (0 when not set)
        if (nowEl && Number.isFinite(price)) nowEl.textContent = formatMoney(price / 100);
        // Sale = a higher compare-at price than the selling price.
        const onSale = Number.isFinite(compare) && compare > price;
        if (wasEl) {
          wasEl.textContent = onSale ? formatMoney(compare / 100) : '';
          wasEl.hidden = !onSale;
        }
        if (wrap) wrap.classList.toggle('cart-upsell__price--on-sale', onSale);
      });
      this.refresh();
    }

    async getCart() {
      const res = await fetch(root + 'cart.js');
      return res.json();
    }

    buildUrl(cart) {
      const collection = this.dataset.collection;
      const sectionId = this.dataset.sectionId;
      if (collection) {
        return root + 'collections/' + collection + '?section_id=' + sectionId;
      }
      if (!cart.items.length) return null;
      const seed = cart.items[cart.items.length - 1].product_id;
      return this.dataset.url + '&product_id=' + seed + '&section_id=' + sectionId;
    }

    async refresh() {
      let cart;
      try {
        cart = await this.getCart();
      } catch (e) {
        this.hide();
        return;
      }
      if (!cart.items || !cart.items.length) {
        this.hide();
        return;
      }
      const url = this.buildUrl(cart);
      if (!url) {
        this.hide();
        return;
      }
      let text;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          this.hide();
          return;
        }
        text = await res.text();
      } catch (e) {
        this.hide();
        return;
      }
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const list = doc.querySelector('.cart-upsell__list');
      if (!list) {
        this.hide();
        return;
      }
      const inCart = new Set(cart.items.map((i) => i.product_id));
      list.querySelectorAll('[data-product-id]').forEach((li) => {
        if (inCart.has(Number(li.dataset.productId))) li.remove();
      });
      const remaining = list.querySelectorAll('.cart-upsell__item');
      if (!remaining.length) {
        this.hide();
        return;
      }
      if (this.body) {
        this.body.innerHTML = '';
        this.body.appendChild(list);
      }
      this.hidden = false;
    }

    hide() {
      this.hidden = true;
      if (this.body) this.body.innerHTML = '';
    }

    async onClick(e) {
      const add = e.target.closest('.cart-upsell__add');
      if (!add || add.tagName === 'A') return; // links go to the PDP
      e.preventDefault();
      // Prefer the inline variant <select> value; fall back to the button's id.
      const item = add.closest('.cart-upsell__item');
      const select = item && item.querySelector('.cart-upsell__select');
      const variantId = select ? select.value : add.dataset.variantId;
      if (!variantId) return;
      add.classList.add('loading');
      add.disabled = true;
      try {
        const res = await fetch(root + 'cart/add.js', {
          ...cartFetchConfig(),
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: drawerSectionNames().join(','),
            sections_url: window.location.pathname,
          }),
        });
        const parsed = await res.json();
        if (!res.ok) {
          add.classList.remove('loading');
          add.disabled = false;
          return;
        }
        renderDrawerSections(parsed);
      } catch (err) {
        console.error(err);
        add.classList.remove('loading');
        add.disabled = false;
      }
    }
  }
  customElements.define('cart-upsell', CartUpsell);

  // ---- Estimate shipping ----------------------------------------------------

  class CartEstimateShipping extends HTMLElement {
    connectedCallback() {
      this.country = this.querySelector('.cart-estimate__country');
      this.province = this.querySelector('.cart-estimate__province');
      this.zip = this.querySelector('.cart-estimate__zip');
      this.results = this.querySelector('.cart-estimate__results');
      const submit = this.querySelector('.cart-estimate__submit');
      if (submit) submit.addEventListener('click', this.onSubmit.bind(this));
    }

    params() {
      const p = new URLSearchParams();
      if (this.zip && this.zip.value) p.set('shipping_address[zip]', this.zip.value.trim());
      if (this.country && this.country.value) p.set('shipping_address[country]', this.country.value);
      if (this.province && this.province.value) p.set('shipping_address[province]', this.province.value.trim());
      return p.toString();
    }

    async onSubmit() {
      const qs = this.params();
      if (!this.results) return;
      this.results.innerHTML = '<p class="cart-estimate__status">' + (STR.shippingCalculating || 'Calculating…') + '</p>';
      try {
        const prep = await fetch(root + 'cart/prepare_shipping_rates.json?' + qs, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });
        if (prep.status === 422) throw new Error(await this.errMsg(prep));
        const rates = await this.poll(qs);
        this.render(rates);
      } catch (e) {
        this.results.innerHTML =
          '<p class="cart-estimate__status cart-estimate__status--error">' +
          this.escape((e && e.message) || STR.shippingError || 'We could not estimate shipping for this address.') +
          '</p>';
      }
    }

    async poll(qs, tries) {
      tries = tries || 0;
      if (tries > 20) throw new Error(STR.shippingError || 'Timed out estimating shipping.');
      const res = await fetch(root + 'cart/async_shipping_rates.json?' + qs);
      if (res.status === 422) throw new Error(await this.errMsg(res));
      if (res.status === 200) {
        const body = await res.json().catch(() => null);
        if (body && Array.isArray(body.shipping_rates)) return body.shipping_rates;
      }
      await new Promise((r) => setTimeout(r, 500));
      return this.poll(qs, tries + 1);
    }

    render(rates) {
      if (!rates.length) {
        this.results.innerHTML = '<p class="cart-estimate__status">' + (STR.shippingNone || 'No shipping rates for this address.') + '</p>';
        return;
      }
      const free = STR.free || 'Free';
      const rows = rates
        .map((r) => {
          const price = parseFloat(r.price);
          const display = price === 0 ? free : formatMoney(price);
          return '<li class="cart-estimate__rate"><span>' + this.escape(r.name) + '</span><span>' + display + '</span></li>';
        })
        .join('');
      this.results.innerHTML = '<ul class="cart-estimate__rates list-unstyled" role="list">' + rows + '</ul>';
    }

    async errMsg(res) {
      try {
        const j = await res.json();
        if (Array.isArray(j.error)) return j.error[0];
        if (typeof j.error === 'string') return j.error;
        if (j.message) return j.message;
      } catch (e) {}
      return STR.shippingError || 'We could not estimate shipping for this address.';
    }

    escape(s) {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    }
  }
  customElements.define('cart-estimate-shipping', CartEstimateShipping);

  // ---- Cart attributes (delivery date + custom field) -----------------------

  async function saveAttribute(name, value) {
    const attributes = {};
    attributes[name] = value;
    const res = await fetch(root + 'cart/update.js', {
      ...cartFetchConfig(),
      body: JSON.stringify({ attributes }),
    });
    return res.json();
  }

  class CartDeliveryDate extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.cart-delivery-date__input');
      if (!this.input) return;
      const today = new Date();
      this.input.min = today.toISOString().split('T')[0];
      this.input.addEventListener('change', () => {
        saveAttribute('Delivery date', this.input.value).catch((e) => console.error(e));
      });
    }
  }
  customElements.define('cart-delivery-date', CartDeliveryDate);

  class CartCustomField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.cart-custom-field__input');
      this.errorEl = this.querySelector('.cart-custom-field__error');
      if (!this.input) return;
      let t;
      this.input.addEventListener('input', () => {
        if (this.errorEl) this.errorEl.hidden = true;
        clearTimeout(t);
        t = setTimeout(() => {
          const label = this.input.getAttribute('placeholder') || 'Note';
          saveAttribute(label, this.input.value).catch((e) => console.error(e));
        }, 400);
      });
    }
  }
  customElements.define('cart-custom-field', CartCustomField);

  // ---- Share cart -----------------------------------------------------------

  class CartShare extends HTMLElement {
    async connectedCallback() {
      this.urlInput = this.querySelector('.cart-share__url');
      this.copyBtn = this.querySelector('.cart-share__copy');
      if (this.copyBtn) this.copyBtn.addEventListener('click', this.copy.bind(this));
      try {
        const cart = await (await fetch(root + 'cart.js')).json();
        if (this.urlInput) this.urlInput.value = this.buildPermalink(cart);
      } catch (e) {
        /* leave blank */
      }
    }

    buildPermalink(cart) {
      const parts = cart.items.map((i) => i.variant_id + ':' + i.quantity);
      const origin = window.location.origin;
      return origin + root + 'cart/' + parts.join(',');
    }

    async copy() {
      if (!this.urlInput || !this.urlInput.value) return;
      try {
        await navigator.clipboard.writeText(this.urlInput.value);
      } catch (e) {
        this.urlInput.select();
        document.execCommand('copy');
      }
      const original = this.copyBtn.textContent;
      this.copyBtn.textContent = STR.shareCopied || 'Copied';
      this.copyBtn.classList.add('cart-share__copy--done');
      setTimeout(() => {
        this.copyBtn.textContent = original;
        this.copyBtn.classList.remove('cart-share__copy--done');
      }, 2000);
    }
  }
  customElements.define('cart-share', CartShare);

  // ---- Reservation countdown ------------------------------------------------

  class CartCountdown extends HTMLElement {
    connectedCallback() {
      const minutes = parseInt(this.dataset.minutes, 10) || 10;
      this.message = this.dataset.message || 'Your selections are held for {timer}.';
      const KEY = 'embelora:cart-countdown';
      let end = parseInt(localStorage.getItem(KEY), 10);
      const now = Date.now();
      if (!end || end < now) {
        end = now + minutes * 60 * 1000;
        localStorage.setItem(KEY, String(end));
      }
      this.end = end;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.max(0, this.end - Date.now());
      if (remaining <= 0) {
        clearInterval(this.timer);
        this.hidden = true;
        return;
      }
      const total = Math.floor(remaining / 1000);
      const m = String(Math.floor(total / 60)).padStart(2, '0');
      const s = String(total % 60).padStart(2, '0');
      this.innerHTML =
        '<span class="cart-countdown__text">' + this.escape(this.message).replace('{timer}', '<strong>' + m + ':' + s + '</strong>') + '</span>';
    }

    escape(s) {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    }
  }
  customElements.define('cart-countdown', CartCountdown);

  // ---- Terms & conditions ---------------------------------------------------

  class CartTerms extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('.cart-terms__checkbox');
      this.errorEl = this.querySelector('.cart-terms__error');
      if (!this.checkbox) return;
      const KEY = 'embelora:cart-terms';
      if (localStorage.getItem(KEY) === 'agreed') this.checkbox.checked = true;
      this.checkbox.addEventListener('change', () => {
        localStorage.setItem(KEY, this.checkbox.checked ? 'agreed' : 'no');
        if (this.checkbox.checked && this.errorEl) this.errorEl.hidden = true;
        saveAttribute('Terms and conditions', this.checkbox.checked ? 'Agreed' : 'Not agreed').catch((e) => console.error(e));
      });
    }
  }
  customElements.define('cart-terms', CartTerms);

  // ---- Line-item variant editor (Edit -> change variant) --------------------
  // /cart/change.js can't swap a variant, so ADD the new variant first
  // (fail-safe), then REMOVE the old line by its key. Quantity + properties +
  // selling plan are carried over. `sections` on the final call refreshes the
  // drawer in one round-trip.
  async function changeLineVariant(key, newVariantId, sections) {
    const cart = await (await fetch(root + 'cart.js')).json();
    const oldLine = (cart.items || []).find((it) => it.key === key);
    if (!oldLine) throw new Error('line-not-found');
    if (String(newVariantId) === String(oldLine.variant_id)) return null; // no-op
    const addBody = { id: Number(newVariantId), quantity: oldLine.quantity };
    if (oldLine.properties && Object.keys(oldLine.properties).length) addBody.properties = oldLine.properties;
    const sp = oldLine.selling_plan_allocation && oldLine.selling_plan_allocation.selling_plan;
    if (sp && sp.id) addBody.selling_plan = sp.id;
    const addRes = await fetch(root + 'cart/add.js', { ...cartFetchConfig(), body: JSON.stringify(addBody) });
    if (!addRes.ok) {
      const err = await addRes.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'add-failed');
    }
    const changeRes = await fetch(root + 'cart/change.js', {
      ...cartFetchConfig(),
      body: JSON.stringify({ id: key, quantity: 0, sections, sections_url: window.location.pathname }),
    });
    return changeRes.json();
  }

  function closeLineEditor(editor) {
    if (!editor) return;
    editor.hidden = true;
    const sel = editor.querySelector('.cart-item__variant-select');
    if (sel) sel.value = editor.dataset.currentVariant;
    const upd = editor.querySelector('.cart-item__update');
    if (upd) upd.disabled = true;
    const err = editor.querySelector('.cart-item__editor-error');
    if (err) err.hidden = true;
    const item = editor.closest('.cart-item');
    const toggle = item && item.querySelector('.cart-item__edit-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', async (e) => {
    if (!e.target.closest) return;

    const editToggle = e.target.closest('.cart-item__edit-toggle');
    if (editToggle) {
      const item = editToggle.closest('.cart-item');
      const editor = item && item.querySelector('.cart-item__editor');
      if (!editor) return;
      const opening = editor.hidden;
      document.querySelectorAll('.cart-item__editor:not([hidden])').forEach((ed) => {
        if (ed !== editor) closeLineEditor(ed);
      });
      if (opening) {
        editor.hidden = false;
        editToggle.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => scrollIntoCartView(editor));
      } else {
        closeLineEditor(editor);
      }
      return;
    }

    const cancelBtn = e.target.closest('.cart-item__edit-cancel');
    if (cancelBtn) {
      closeLineEditor(cancelBtn.closest('.cart-item__editor'));
      return;
    }

    const updateBtn = e.target.closest('.cart-item__update');
    if (updateBtn) {
      const editor = updateBtn.closest('.cart-item__editor');
      const sel = editor && editor.querySelector('.cart-item__variant-select');
      const key = editor && editor.dataset.lineKey;
      const newVariantId = sel && sel.value;
      if (!editor || !key || !newVariantId || String(newVariantId) === String(editor.dataset.currentVariant)) return;
      const err = editor.querySelector('.cart-item__editor-error');
      if (err) err.hidden = true;
      updateBtn.classList.add('loading');
      updateBtn.disabled = true;
      try {
        // Refresh both surfaces so the drawer and the cart page never drift out
        // of sync — editing on one keeps the other's line keys + variant current.
        const sectionList = [...new Set([...drawerSectionNames(), ...pageSectionNames()])];
        const updated = await changeLineVariant(key, newVariantId, sectionList.join(','));
        if (updated) {
          renderDrawerSections(updated);
          renderPageSections(updated);
        }
      } catch (ex) {
        updateBtn.classList.remove('loading');
        updateBtn.disabled = false;
        if (err) {
          err.textContent = STR.variantUpdateError || "That option can't be added right now.";
          err.hidden = false;
          requestAnimationFrame(() => scrollIntoCartView(err));
        }
      }
      return;
    }
  });

  // Enable "Update" only once the chosen variant differs from the current one.
  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('.cart-item__variant-select');
    if (!sel) return;
    const editor = sel.closest('.cart-item__editor');
    const upd = editor && editor.querySelector('.cart-item__update');
    if (upd) upd.disabled = String(sel.value) === String(editor.dataset.currentVariant);
  });

  // ---- Checkout gating (terms + required custom field) -----------------------

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || form.id !== 'CartDrawer-Form') return;

      const terms = document.querySelector('cart-terms');
      if (terms) {
        const cb = terms.querySelector('.cart-terms__checkbox');
        if (cb && !cb.checked) {
          e.preventDefault();
          const err = terms.querySelector('.cart-terms__error');
          if (err) err.hidden = false;
          terms.classList.add('cart-terms--shake');
          setTimeout(() => terms.classList.remove('cart-terms--shake'), 500);
          cb.focus();
          return;
        }
      }

      const cf = document.querySelector('cart-custom-field[data-required="true"]');
      if (cf) {
        const input = cf.querySelector('.cart-custom-field__input');
        if (input && !input.value.trim()) {
          e.preventDefault();
          const err = cf.querySelector('.cart-custom-field__error');
          if (err) {
            err.textContent = STR.customFieldRequired || 'This field is required.';
            err.hidden = false;
          }
          openToolPanel(cf, input);
          requestAnimationFrame(() => scrollIntoCartView(err || cf));
          return;
        }
      }
    },
    true
  );
})();
